import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Table, TextInput } from "@skyportal/ui-kit";
import AutocompleteTableFilter from "components/AutocompleteTableFilter";
import PaginationBox from "components/PaginationBox";
import {
  CreateBucketModal,
  DeleteBucketModal,
  EditBucketModal,
  ExportBucketsModal,
} from "containers/BucketsPageContent/modals";
import useBucketsColumnConfig from "hooks/useBucketsTableColumnConfig";
import { useTranslation } from "react-i18next";
import { formatStorageSize } from "utils/helpers";
import { bucketsSortHandler } from "utils/sort";

import { Bucket, mapBucketDtoToBucket } from "../mappers/bucketsMapper";
import { mockApiResponse } from "./mockData";

import styles from "../BucketsPageContent.module.css";

const BucketsTable = () => {
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [bucketToEdit, setBucketToEdit] = useState<Bucket | null>(null);
  const [bucketToDelete, setBucketToDelete] = useState<Bucket | null>(null);
  const { t } = useTranslation("storageOverview");

  const data = useMemo(() => mockApiResponse.buckets.map(mapBucketDtoToBucket), []);
  const existingBucketNames = useMemo(() => data?.map((bucket) => bucket.name), [data]);
  const filteredData = useMemo(() => {
    return data.filter((bucket) => {
      const matchesSearch = bucket.name.toLowerCase().includes(search.toLowerCase());

      const matchesLocation = location.length === 0 || location.includes(bucket.location);

      return matchesSearch && matchesLocation;
    });
  }, [data, search, location]);

  const sortedData = useMemo(() => {
    if (!sortField) return [...filteredData];

    const sorter = bucketsSortHandler[sortField as keyof typeof bucketsSortHandler];

    if (!sorter) return [...filteredData];

    return [...filteredData].sort(sorter(sortOrder));
  }, [filteredData, sortField, sortOrder]);

  const pageCount = Math.max(1, Math.ceil(sortedData.length / perPage));

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return sortedData.slice(start, start + perPage);
  }, [sortedData, currentPage, perPage]);

  const handlePerPageChange = useCallback((value: number) => {
    setPerPage(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((value: number) => {
    setCurrentPage(value);
  }, []);

  const handleSortFieldChange = useCallback(
    (field: string) => {
      if (field === sortField) {
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortOrder("asc");
      }
    },
    [sortField]
  );

  const totalSize = useMemo(() => filteredData.reduce((acc, b) => acc + b.size, 0), [filteredData]);

  const handleEdit = useCallback((bucket: Bucket) => {
    setBucketToEdit(bucket);
  }, []);

  const handleDelete = useCallback((bucket: Bucket) => {
    setBucketToDelete(bucket);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleLocationChange = useCallback((_: unknown, values: { value: string }[]) => {
    setLocation(values.map((value) => value.value));
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [currentPage, pageCount]);

  const columns = useBucketsColumnConfig({
    onEdit: handleEdit,
    onDelete: handleDelete,
  });

  return (
    <>
      <div className={styles.cardContent}>
        <div className={styles.tableBlock}>
          <div className={styles.filtersBlock}>
            <div className={styles.filtersLeft}>
              <div className={styles.filterBar}>
                <AutocompleteTableFilter
                  list={data}
                  currentFilterValues={location}
                  keyIndex="location"
                  onChange={handleLocationChange}
                  testIdPrefix="bucketsFilter"
                  localeObjectName="storageOverview"
                />
              </div>

              <div className={styles.filterBar}>
                <div className={styles.filterLabel}>{t("filterLabels.search")}</div>
                <TextInput
                  className={styles.filterInput}
                  height={37}
                  value={search}
                  onChange={handleSearchChange}
                  placeholder={t("placeholders.search")}
                />
              </div>
            </div>

            <div className={styles.actionsBlock}>
              <Button type="primary" className={styles.actionBtn} onClick={() => setIsCreateOpen(true)}>
                {t("actions.create")}
              </Button>
              <Button className={styles.actionBtn} onClick={() => setIsExportOpen(true)}>
                {" "}
                {t("actions.export")}
              </Button>
            </div>
          </div>

          <div className={styles.totalSize}>
            {t("labels.total")}: {formatStorageSize(totalSize)}
          </div>
          <Table
            rowKey="id"
            dataSource={paginatedData}
            columns={columns}
            sortField={sortField}
            sortOrder={sortOrder}
            onSortFieldChange={handleSortFieldChange}
          />

          <PaginationBox
            perPage={perPage}
            currentPage={currentPage}
            pageCount={pageCount}
            onPerPageChange={handlePerPageChange}
            onChangePage={handlePageChange}
          />
        </div>
        <CreateBucketModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          accountOptions={[]}
          locationOptions={[]}
          existingBucketNames={existingBucketNames}
        />

        <EditBucketModal open={!!bucketToEdit} bucket={bucketToEdit} onClose={() => setBucketToEdit(null)} />

        <DeleteBucketModal
          open={!!bucketToDelete}
          bucket={bucketToDelete}
          onClose={() => setBucketToDelete(null)}
          onConfirm={() => {
            console.log("DELETE CONFIRMED", bucketToDelete);
            setBucketToDelete(null);
          }}
        />

        <ExportBucketsModal open={isExportOpen} onClose={() => setIsExportOpen(false)} />
      </div>
    </>
  );
};

export default BucketsTable;
